import { FC } from 'react';
import CandidateRow from './CandidateRow';
import Pagination from './Pagination';
import RowSkeleton from './RowSkeleton';
import { ResultsPanelProps, TabKey } from './types';
import { formatLocalTime, nightlyMaintenance } from './utils';

// Pagination page size — keep in sync with index.tsx's PAGE_SIZE
// (README §Interactions & Behavior: "Pagination: 8 per page").
const PAGE_SIZE = 8;

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const HEADER_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 12,
  flexWrap: 'wrap',
  gap: 6,
};

const GUARDRAIL_CARD_STYLE: React.CSSProperties = {
  background: '#FEF0D9',
  border: '1px solid #FBDBA0',
  borderRadius: 8,
  padding: '20px 16px',
  textAlign: 'center',
};

const EMPTY_STATE_STYLE: React.CSSProperties = {
  textAlign: 'center',
  padding: '32px 16px',
  color: '#667085',
};

const NON_MATCHES_TOGGLE_STYLE: React.CSSProperties = {
  display: 'block',
  margin: '4px auto 12px',
  background: 'none',
  border: '1px solid #E2E5EA',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  color: '#667085',
  cursor: 'pointer',
};

const SKELETON_ROW_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const SkeletonRows: FC = () => (
  <div>
    {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={idx} style={SKELETON_ROW_STYLE}>
        <div style={{ width: 78, flexShrink: 0 }} />
        <div style={{ flex: '1 1 220px', minWidth: 220 }}>
          <RowSkeleton />
        </div>
        <div style={{ width: 186, flexShrink: 0 }} />
      </div>
    ))}
  </div>
);

/**
 * Results list shared by all four tabs (Batch Matches, Search Pool,
 * Shortlist, Declined) — header (count/range), the search too-broad
 * guardrail card (gold, STORY-2A-SPEC rule 3), empty state guidance,
 * skeleton rows while a fetch is in flight, the row list itself, and
 * pagination (8/page). Checkboxes + bulk-select (Shortlist tab) and
 * dispositionMode row actions (Apply/Restore) are Story 2b additions,
 * opt-in via optional props — Batch/Search callers are unaffected.
 * dispositionMode 'applicants' (kan-119) is passed straight through to the
 * rows, which swap their action buttons for the application date + status.
 */
const EMPTY_STATE_COPY: Record<TabKey, { title: string; body: string }> = {
  batch: {
    title: 'No matches found',
    body: 'Try widening the location radius, lowering the minimum years of experience, or removing a skill filter.',
  },
  search: {
    title: 'No matches found',
    body: 'Try widening the location radius, lowering the minimum years of experience, removing a skill filter, or broadening your search term.',
  },
  // Collaborated Search post-run with seeds present but zero survivors. The
  // "job order has no interviews at all" case is a separate state (noInterviews
  // below), not this one.
  collab: {
    title: 'No matches found',
    body: 'No candidates similar to the interviewed set were found. Try widening the location radius, lowering the minimum years of experience, or removing a skill filter.',
  },
  shortlist: {
    title: 'No shortlisted candidates yet',
    body: 'Add candidates to the shortlist from Batch Matches or Search Pool.',
  },
  declined: {
    title: 'No declined candidates',
    body: 'Candidates you decline from Batch Matches or Search Pool will show up here.',
  },
};

const ResultsPanel: FC<ResultsPanelProps> = ({
  view,
  hydrated,
  total,
  page,
  onPage,
  loading,
  tooBroad,
  backendError,
  awaitingRun,
  noInterviews,
  collabSeeds,
  matchCount,
  nonMatchCount,
  showNonMatches,
  onToggleNonMatches,
  onOpenDetail,
  selectable,
  selectedIds,
  onToggleSelect,
  dispositionMode,
  onApplyRow,
  onRestoreRow,
  onShortlistRow,
  onDeclineRow,
  onApplicantScoreUpdated,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * PAGE_SIZE, total);
  // Batch-only: matchCount drives the header count/pageCount (via `total`,
  // set by index.tsx's effectiveTotal); non-matches stay hidden behind the
  // expander below unless the recruiter opts in. Search/Shortlist/Declined
  // pass nonMatchCount === 0, so this never renders for them.
  const isBatch = view === 'batch';

  return (
    <div style={CARD_STYLE}>
      <div style={HEADER_ROW_STYLE}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#1F2937' }}>Recommended candidates</span>
        <span style={{ fontSize: 12, color: '#667085' }}>
          {total} match{total === 1 ? '' : 'es'}
          {total > 0 ? ` · ${rangeStart}–${rangeEnd} of ${total}` : ''}
          {isBatch && showNonMatches ? ' · showing non-matches' : ''}
          {/* Collaborated Search seed accounting: seeds recsys actually had
              vectors for, out of the seeds the page sent (kan-82). */}
          {view === 'collab' && collabSeeds
            ? ` · Based on ${collabSeeds.used} of ${collabSeeds.total} interviewed candidates.`
            : ''}
        </span>
      </div>

      {loading ? (
        <SkeletonRows />
      ) : backendError ? (
        // The search backend failed — say so instead of "No matches found"
        // (a recruiter cannot tell an outage from an empty search otherwise).
        <div style={GUARDRAIL_CARD_STYLE} role="alert" data-testid="backend-error">
          <div style={{ fontSize: 13, fontWeight: 700, color: '#B25E09' }}>
            {backendError.duringMaintenance
              ? 'Candidate Search is in its nightly maintenance window'
              : 'The search service is not responding'}
          </div>
          <div style={{ fontSize: 12, color: '#667085', marginTop: 6 }}>
            {backendError.duringMaintenance ? (
              <>
                The résumé index is rebuilt every night ({formatLocalTime(nightlyMaintenance().start)} –{' '}
                {formatLocalTime(nightlyMaintenance().end)}); searches fail or return nothing until it
                finishes. Nothing is wrong with your filters — try again after{' '}
                {formatLocalTime(nightlyMaintenance().end)}.
              </>
            ) : (
              <>
                The search request failed{backendError.status ? ` (HTTP ${backendError.status})` : ''}. Try
                again in a few minutes; if it keeps failing, let the team know.
              </>
            )}
          </div>
        </div>
      ) : awaitingRun ? (
        <div style={EMPTY_STATE_STYLE}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>Ready when you are</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {/* Each explicit-run tab names its own trigger button. */}
            {view === 'collab' ? (
              <>
                Pick a company and job order, then hit <strong>Find Similar Candidates</strong> to see
                candidates similar to the ones you&apos;ve interviewed.
              </>
            ) : (
              <>
                Pick a company and job order, then hit <strong>Refresh</strong> to see recommended
                candidates.
              </>
            )}
          </div>
        </div>
      ) : noInterviews ? (
        // Collaborated Search gate (kan-82): the job order has no interviews,
        // so recsys was never called. Precedes the generic empty state, which
        // would otherwise read as "we searched and found nothing".
        <div style={EMPTY_STATE_STYLE}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            No interviews on this job order yet.
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Collaborated Search finds candidates similar to the ones you&apos;ve already interviewed.
          </div>
        </div>
      ) : tooBroad ? (
        <div style={GUARDRAIL_CARD_STYLE}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#B25E09' }}>Your search is too broad</div>
          <div style={{ fontSize: 12, color: '#667085', marginTop: 6 }}>
            Add more specific terms (skills, titles, or boolean operators) and try again.
          </div>
        </div>
      ) : isBatch && matchCount === 0 && nonMatchCount > 0 && !showNonMatches ? (
        <div style={EMPTY_STATE_STYLE}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            No strong or possible matches
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {nonMatchCount} candidate{nonMatchCount === 1 ? '' : 's'} were judged not a match.
          </div>
          <button type="button" style={{ ...NON_MATCHES_TOGGLE_STYLE, marginTop: 12 }} onClick={onToggleNonMatches}>
            {showNonMatches ? 'Hide non-matches' : `Show ${nonMatchCount} non-matches`}
          </button>
        </div>
      ) : total === 0 ? (
        <div style={EMPTY_STATE_STYLE}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>{EMPTY_STATE_COPY[view].title}</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{EMPTY_STATE_COPY[view].body}</div>
        </div>
      ) : (
        <>
          {/* `hydrated` can be transiently empty for one render if `page` is
              out of range for a shrunken result set (e.g. after a filter
              change) — Pagination's self-clamping effect below corrects
              `page` via `onPage`, which re-renders this list on the next
              tick. Total (not the current page's row count) is the source of
              truth for whether there ARE matches. */}
          <div>
            {hydrated.map((item) => (
              <CandidateRow
                key={item.row.candidateId}
                item={item}
                view={view}
                onOpenDetail={onOpenDetail}
                selectable={selectable}
                selected={selectedIds?.has(item.row.candidateId)}
                onToggleSelect={onToggleSelect}
                dispositionMode={dispositionMode}
                onApplyRow={onApplyRow}
                onRestoreRow={onRestoreRow}
                onShortlistRow={onShortlistRow}
                onDeclineRow={onDeclineRow}
                onApplicantScoreUpdated={onApplicantScoreUpdated}
              />
            ))}
          </div>
          {isBatch && nonMatchCount > 0 && (
            <button type="button" style={NON_MATCHES_TOGGLE_STYLE} onClick={onToggleNonMatches}>
              {showNonMatches ? 'Hide non-matches' : `Show ${nonMatchCount} non-matches`}
            </button>
          )}
          <Pagination page={page} pageCount={pageCount} onPage={onPage} />
        </>
      )}
    </div>
  );
};

export default ResultsPanel;
